import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App, Button, Flex, Input, Segmented, Select, Space, Table, Tooltip, Typography } from "antd";
import { useDeferredValue, useState } from "react";
import { useNavigate } from "react-router";
import { api, unwrap, type ChannelPlatformName } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { ExportButton } from "@/components/list/ExportButton";
import { useListPage } from "@/components/list/useListPage";
import { useI18n } from "@/i18n";
import { useUrlFilter } from "@/lib/useUrlFilter";
import { PLATFORMS, useConnections } from "../channels/hooks";
import { PlatformLabel } from "../channels/PlatformBadge";
import { PUBLISH_PLATFORMS } from "../products/Products";
import { CreateAllSkus } from "./CreateAllSkus";
import { MapModal, MappedTo, usePlatformActions, type PlatformSKU } from "./mapping";
import { PriceSyncTag } from "./prices";

type MappedFilter = "all" | "unmapped" | "mapped";

/** A listing that can become a Zerpid product: on a store products publish to, and none yet. */
const productable = (p: PlatformSKU) => !p.productId && PUBLISH_PLATFORMS.includes(p.platform);

/** Listing toko: every store's SKUs, what they sell, and the product they are (PRD-2). */
export function PlatformSkus({ unmappedCount }: { unmappedCount: number }) {
  const { t, formatMoney, formatNumber } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const [creatingAll, setCreatingAll] = useState(false);
  const manage = can("catalog.manage");
  const connections = useConnections();
  const [mapped, setMapped] = useUrlFilter<MappedFilter>("mapped", "unmapped", ["all", "unmapped", "mapped"]);
  const [store, setStore] = useUrlFilter<string>("store", "");
  const [platform, setPlatform] = useUrlFilter<ChannelPlatformName | "">("platform", "", ["", ...PLATFORMS]);
  const page = useListPage();
  const [search, setSearch] = useUrlFilter<string>("pq", "");
  const q = useDeferredValue(search.trim());
  const [selected, setSelected] = useState<string[]>([]);
  const [mapping, setMapping] = useState<string[] | null>(null);
  const { createFromPlatform, untrack } = usePlatformActions();

  const filters = {
    ...(mapped !== "all" ? { mapped: mapped === "mapped" } : {}),
    ...(store ? { connectionId: store } : {}),
    ...(platform ? { platform } : {}),
    ...(q ? { q } : {}),
  };
  const list = useQuery({
    queryKey: ["platform-skus", filters, page.query],
    queryFn: () => unwrap(api.GET("/v1/catalog/platform-skus", { params: { query: { ...filters, ...page.query } } })),
    placeholderData: keepPreviousData,
  });
  const rows = list.data?.items ?? [];
  const picked = rows.filter((r) => selected.includes(r.id));
  const clear = () => setSelected([]);

  // "Jadikan produk": each listing once, per store, 20 at a time (the import's limit).
  const makeProducts = useMutation({
    mutationFn: async (from: PlatformSKU[]) => {
      const byStore = new Map<string, Set<string>>();
      for (const r of from.filter(productable)) byStore.set(r.connectionId, (byStore.get(r.connectionId) ?? new Set()).add(r.listingId));
      const results = [];
      for (const [connectionID, listings] of byStore) {
        const ids = [...listings];
        for (let i = 0; i < ids.length; i += 20) {
          const res = await unwrap(
            api.POST("/v1/products/connections/{connectionID}/import", { params: { path: { connectionID } }, body: { externalIds: ids.slice(i, i + 20) } }),
          );
          results.push(...res.results);
        }
      }
      return results;
    },
    onSuccess: (results) => {
      const made = results.filter((r) => r.productId);
      const failed = results.filter((r) => !r.productId);
      if (made.length > 0) void message.success(t("listings.madeProducts", { n: formatNumber(made.length) }));
      if (failed.length > 0) void message.warning(t("listings.madeProductsFailed", { n: formatNumber(failed.length), error: failed[0]?.error ?? "" }));
      for (const key of ["platform-skus", "products"]) void queryClient.invalidateQueries({ queryKey: [key] });
      clear();
    },
    onError: (err) => void message.error(errorText(err)),
  });
  const pickedProductable = picked.filter(productable);

  return (
    <>
      <Flex gap={12} wrap style={{ marginBottom: 16 }} justify="space-between">
        <Space wrap>
          <Segmented<MappedFilter>
            value={mapped}
            onChange={(v) => {
              setMapped(v);
              clear();
            }}
            options={[
              { value: "unmapped", label: t("catalog.platform.filter.unmapped", { n: formatNumber(unmappedCount) }) },
              { value: "mapped", label: t("catalog.platform.filter.mapped") },
              { value: "all", label: t("catalog.platform.filter.all") },
            ]}
          />
          <Select<ChannelPlatformName | "">
            value={platform}
            onChange={setPlatform}
            style={{ width: 170 }}
            options={[{ value: "", label: t("channels.colMarketplace") }, ...PLATFORMS.map((x) => ({ value: x, label: t(`platform.${x}`) }))]}
          />
          <Select<string>
            value={store}
            onChange={setStore}
            style={{ width: 200 }}
            options={[
              { value: "", label: t("orders.allStores") },
              ...(connections.data?.items ?? []).filter((c) => !platform || c.platform === platform).map((c) => ({ value: c.id, label: c.name })),
            ]}
          />
          <Input.Search allowClear placeholder={t("catalog.platform.search")} value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 280 }} />
          {manage && mapped === "unmapped" && unmappedCount > 0 && (
            <Tooltip title={t("listings.createAll.tip")}>
              <Button onClick={() => setCreatingAll(true)}>{t("listings.createAll")}</Button>
            </Tooltip>
          )}
        </Space>
        <ExportButton
          path="/v1/catalog/platform-skus/export"
          query={filters}
          filename={t("catalog.view.platform")}
          columns={[
            { key: "platform", label: t("channels.colMarketplace"), values: Object.fromEntries(PLATFORMS.map((x) => [x, t(`platform.${x}`)])) },
            { key: "shopName", label: t("orders.store") },
            { key: "listingName", label: t("catalog.platform.product") },
            { key: "variant", label: t("catalog.platform.variant") },
            { key: "sellerSku", label: t("catalog.platform.sellerSku") },
            { key: "listingId", label: t("catalog.platform.listingId") },
            { key: "skuId", label: t("catalog.platform.skuId") },
            { key: "price", label: t("catalog.platform.price"), kind: "number" },
            { key: "platformStock", label: t("inventory.platformStock"), kind: "number" },
            { key: "mappedTo.code", label: t("catalog.platform.mappedCode") },
            { key: "mappedTo.name", label: t("catalog.platform.mappedName") },
          ]}
        />
        {manage && picked.length > 0 && (
          <Space wrap>
            <Typography.Text type="secondary">{t("orders.selectedCount", { n: picked.length })}</Typography.Text>
            <Button danger onClick={() => untrack(picked.map((p) => p.id), t("catalog.untrackTitle", { n: picked.length }), clear)}>
              {t("catalog.untrackSelected", { n: picked.length })}
            </Button>
            {pickedProductable.length > 0 && (
              <Button loading={makeProducts.isPending} onClick={() => makeProducts.mutate(picked)}>
                {t("listings.makeProductN", { n: pickedProductable.length })}
              </Button>
            )}
            <Tooltip title={t("catalog.createFromPlatformHelp")}>
              <Button loading={createFromPlatform.isPending} onClick={() => createFromPlatform.mutate(picked.map((p) => p.id), { onSuccess: clear })}>
                {t("catalog.createFromPlatform", { n: picked.length })}
              </Button>
            </Tooltip>
            <Button type="primary" onClick={() => setMapping(picked.map((p) => p.id))}>
              {t("catalog.mapSelected", { n: picked.length })}
            </Button>
          </Space>
        )}
      </Flex>

      <Table<PlatformSKU>
        rowKey="id"
        loading={list.isFetching && !list.data}
        dataSource={rows}
        pagination={page.pagination(list.data)}
        scroll={{ x: 1150 }}
        locale={{ emptyText: t("catalog.platform.empty") }}
        rowSelection={manage ? { selectedRowKeys: selected, onChange: (keys) => setSelected(keys as string[]) } : undefined}
        columns={[
          {
            title: t("orders.store"),
            width: 170,
            render: (_, p) => (
              <Space orientation="vertical" size={0}>
                <PlatformLabel platform={p.platform} />
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {p.shopName}
                </Typography.Text>
              </Space>
            ),
          },
          {
            title: t("catalog.platform.product"),
            render: (_, p) => (
              <Space orientation="vertical" size={0}>
                <span>{p.listingName}</span>
                {p.variant && (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {p.variant}
                  </Typography.Text>
                )}
              </Space>
            ),
          },
          { title: t("catalog.platform.sellerSku"), dataIndex: "sellerSku", width: 160, render: (v: string) => <span className="z-num">{v || "—"}</span> },
          {
            title: t("catalog.platform.price"),
            dataIndex: "price",
            width: 150,
            align: "right",
            render: (v: number | null, p) => (
              <Space orientation="vertical" size={2} align="end">
                <span className="z-num">{v === null ? "—" : formatMoney(v, "IDR")}</span>
                <PriceSyncTag p={p} />
              </Space>
            ),
          },
          {
            title: t("catalog.platform.stock"),
            dataIndex: "platformStock",
            width: 110,
            align: "right",
            render: (v: number | null) => <span className="z-num">{v === null ? "—" : formatNumber(v)}</span>,
          },
          { title: t("catalog.platform.mappedTo"), width: 220, render: (_, p) => <MappedTo p={p} /> },
          {
            title: t("listings.product"),
            width: 150,
            render: (_, p) =>
              p.productId ? (
                <Typography.Link onClick={() => navigate(`/app/products/${p.productId}`)}>{t("listings.openProduct")}</Typography.Link>
              ) : manage && productable(p) ? (
                <Tooltip title={t("listings.makeProductTip")}>
                  <Button size="small" loading={makeProducts.isPending && makeProducts.variables?.[0]?.id === p.id} onClick={() => makeProducts.mutate([p])}>
                    {t("listings.makeProduct")}
                  </Button>
                </Tooltip>
              ) : (
                <Tooltip title={PUBLISH_PLATFORMS.includes(p.platform) ? undefined : t("listings.productUnsupported")}>
                  <Typography.Text type="secondary">—</Typography.Text>
                </Tooltip>
              ),
          },
          ...(manage
            ? [
                {
                  width: 110,
                  render: (_: unknown, p: PlatformSKU) =>
                    p.mappedTo ? (
                      <Button size="small" onClick={() => untrack([p.id], t("catalog.unmapTitle", { name: p.sellerSku || p.listingName }))}>
                        {t("catalog.unmap")}
                      </Button>
                    ) : (
                      <Button size="small" type="primary" ghost onClick={() => setMapping([p.id])}>
                        {t("catalog.map")}
                      </Button>
                    ),
                },
              ]
            : []),
        ]}
      />
      <CreateAllSkus
        open={creatingAll}
        filters={{ ...(store ? { connectionId: store } : {}), ...(platform ? { platform } : {}), ...(q ? { q } : {}) }}
        onClose={() => setCreatingAll(false)}
      />
      <MapModal
        ids={mapping}
        onClose={(done) => {
          setMapping(null);
          if (done) clear();
        }}
      />
    </>
  );
}
